/* eslint-disable sonarjs/no-duplicate-string */
import { html, unsafeCSS, nothing } from 'lit';
import { state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { ScopedRegistryHost } from '@lit-labs/scoped-registry-mixin';
import { LeafElement } from '@cigna/dcws_leaf-web-components/components/leaf-element.js';
import { LeafAlert } from '@cigna/dcws_leaf-web-components/components/alert/alert.component.js';
import { LeafButton } from '@cigna/dcws_leaf-web-components/components/button/button.component.js';
import { LeafLabel } from '@cigna/dcws_leaf-web-components/components/label/label.component.js';
import { LeafInput } from '@cigna/dcws_leaf-web-components/components/input/input.component.js';
import { LeafSelect } from '@cigna/dcws_leaf-web-components/components/select/select.component.js';
import { LeafSelectOption } from '@cigna/dcws_leaf-web-components/components/select-option/select-option.component.js';
import { LeafRadiogroup } from '@cigna/dcws_leaf-web-components/components/radiogroup/radiogroup.component.js';
import { LeafRadio } from '@cigna/dcws_leaf-web-components/components/radio/radio.component.js';
import { LeafValidationMessage } from '@cigna/dcws_leaf-web-components/components/validation-message/validation-message.component.js';
import { LeafTab } from '@cigna/dcws_leaf-web-components/components/tabs/tabs.component.js';
import { LeafTabItem } from '@cigna/dcws_leaf-web-components/components/tabs/tab-item.component.js';
import { LeafTabSection } from '@cigna/dcws_leaf-web-components/components/tabs/tab-section.component.js';
import { CHCSearch } from '@cigna/dcws_leaf-web-components/chc-components/search/search.component.js';
import { isMobile } from '@cigna/dcws_leaf-web-components/utilities/is-mobile.js';
import { I18nMixin } from '../../utils/i18n-mixin.js';
import { GlossaryData, Word } from './interfaces.js';
import styles from './glossary.scss?inline';

// TODO: Find out how to use ScopedRegistry with cloned elements
import '@cigna/dcws_leaf-web-components/components/select/select.js';
import '@cigna/dcws_leaf-web-components/components/select-option/select-option.js';

const escapeRegExp = (string: string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
};

export class CHCGlossary extends ScopedRegistryHost(I18nMixin(LeafElement)) {
  static override styles = unsafeCSS(styles);
  static elementDefinitions = {
    'leaf-alert': LeafAlert,
    'leaf-button': LeafButton,
    'leaf-label': LeafLabel,
    'leaf-input': LeafInput,
    'leaf-select': LeafSelect,
    'leaf-select-option': LeafSelectOption,
    'leaf-radiogroup': LeafRadiogroup,
    'leaf-radio': LeafRadio,
    'leaf-validation-message': LeafValidationMessage,
    'leaf-tab': LeafTab,
    'leaf-tab-item': LeafTabItem,
    'leaf-tab-section': LeafTabSection,
    'chc-search': CHCSearch,
  };
  data?: GlossaryData;
  wordTab = 'word';
  categoryTab = 'category';
  letterTab = 'letter';
  unCategorizedId?: number;
  error = false;

  protected override ns = 'glossary';

  @state()
  filterWordData?: Array<Word> = [];

  @state()
  selectedAnchor!: string;

  @state()
  selectedCategory!: string;

  @state()
  selectedLetter!: string;

  @state()
  searchTerm = '';

  @state()
  private activeTab = 'word';

  @state()
  private variant = isMobile() ? '' : 'columns';

  override onNamespacesLoaded = () => {
    this.data = this.i18n?.getLanguageData(this.ns) as GlossaryData;
  };

  constructor() {
    super();

    window.addEventListener('hashchange', this.onHashChange, false);
    window.addEventListener('resize', () => (this.variant = isMobile() ? '' : 'columns'));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  findWord = (words: any[] | undefined, anchor: string) => words?.find((wordObj) => wordObj.anchor === anchor);

  private onHashChange = async () => {
    const hash = window.location.hash.substring(1).toLowerCase();
    const [tab, section] = hash.split('_');

    switch (tab) {
      case this.categoryTab: {
        if (tab && section) this.selectedCategory = hash;
        else this.selectedCategory = this.categoryTab;

        this.activeTab = this.categoryTab;
        break;
      }
      case this.letterTab: {
        this.selectedCategory = this.categoryTab;
        this.activeTab = this.letterTab;
        break;
      }
      default: {
        if (hash !== this.wordTab) {
          const wordObj = this.findWord(this.data?.words, hash);
          const searchInputElement = this.shadowRoot
            ?.querySelector('chc-search')
            ?.shadowRoot?.querySelector('input') as HTMLInputElement;

          if (wordObj) {
            this.selectedAnchor = hash;
            searchInputElement.value = wordObj.word;
            this.searchTerm = wordObj.word;
            searchInputElement.focus();
          } else {
            console.error(`ERROR: can't find word from hash: ${hash}`);
          }
        }

        this.selectedCategory = this.categoryTab;
        this.activeTab = this.wordTab;

        window.scrollTo({
          top: 0,
          left: 0,
        });
      }
    }
  };

  handleRadioInputChange = (event: Event) => {
    // @ts-ignore - TODO - fix typing
    const input: HTMLInputElement = event.target;
    window.location.hash = input.value;
    this.selectedCategory = input.value;
  };

  handleInputChange(event: { detail: { value: string } }) {
    if (this.searchTerm !== '' && this.selectedAnchor !== '') this.selectedAnchor = '';
    const value = event.detail.value;
    if (value.length >= 3) {
      this.searchTerm = value;
    } else {
      this.searchTerm = '';
    }
  }

  private renderSearchInput() {
    return html` <div class="chcapp-glossary__search-input-container">
      <chc-search value="${this.searchTerm}" @onchange="${this.handleInputChange}"
        >${this.data?.app.searchHelp}</chc-search
      >
    </div>`;
  }

  private renderWords() {
    const result = this.data?.words.filter(
      ({ anchor, definition, word }) =>
        (this.searchTerm !== '' &&
          this.selectedAnchor === '' &&
          (new RegExp(escapeRegExp(this.searchTerm), 'gi').test(definition) ||
            new RegExp(escapeRegExp(this.searchTerm), 'gi').test(word))) ||
        anchor === this.selectedAnchor,
    );

    return html` ${result?.map(
      (content) =>
        html` <dl>
          <dt>
            <a href="#${content.anchor}">
              ${unsafeHTML(
                content.word.replace(
                  new RegExp(`(${escapeRegExp(this.searchTerm)})`, 'gi'),
                  `<span class="highlight">$1</span>`,
                ),
              )}
            </a>
          </dt>
          <dl>
            ${unsafeHTML(
              content.definition.replace(
                new RegExp(`(>[^<]*)(${escapeRegExp(this.searchTerm)})([^<]*)`, 'gi'),
                '$1<span class="highlight">$2</span>$3',
              ),
            )}
          </dl>
        </dl>`,
    )}`;
  }

  private renderCategories() {
    const categories = this.data?.categories.sort((a, b) => a.name.localeCompare(b.name)) || [];
    const middleIndex = Math.ceil(categories?.length / 2);
    const radioGroup1 = categories.slice(0, middleIndex);
    const radioGroup2 = categories.slice(middleIndex);

    if (this.selectedCategory) {
      return html` <form>
          <leaf-radiogroup
            variant="${this.variant}"
            name="leaf-radiogroup-category"
            id="leaf-radiogroup-category"
            value="${this.selectedCategory}"
            required
          >
            <div>
              <leaf-radio value="${this.categoryTab}" @click=${this.handleRadioInputChange}
                >${this.data?.app.allLetterLabel}</leaf-radio
              >
              ${radioGroup1.map(
                (category) =>
                  html`<leaf-radio value="${this.categoryTab}_${category.id}" @click=${this.handleRadioInputChange}
                    >${category.name}</leaf-radio
                  >`,
              )}
            </div>
            <div>
              ${radioGroup2.map(
                (category) =>
                  html`<leaf-radio value="${this.categoryTab}_${category.id}" @click=${this.handleRadioInputChange}
                    >${category.name}</leaf-radio
                  >`,
              )}
              <leaf-radio value="${this.categoryTab}_${this.unCategorizedId}" @click=${this.handleRadioInputChange}>
                ${this.data?.app.unCategorizedLabel}
              </leaf-radio>
            </div>
          </leaf-radiogroup>
        </form>
        <div>
          ${this.selectedCategory === this.categoryTab
            ? html`${categories.map(
                  (category) =>
                    html` <h3>${category.name ? category.name : this.data?.app.unCategorizedLabel}</h3>
                      ${this.data?.words
                        .filter((wordObj) => wordObj.categories && wordObj.categories.includes(category.id))
                        .map(
                          ({ anchor, definition, word }) =>
                            html` <div>
                              <a href="#${anchor}">${word}</a>
                              <br />${unsafeHTML(definition)}
                            </div>`,
                        )}`,
                )}
                <h3>${this.data?.app.unCategorizedLabel}</h3>
                ${this.data?.words
                  .filter((wordObj) => !wordObj.categories)
                  .map(
                    ({ anchor, definition, word }) =>
                      html` <div>
                        <a href="#${anchor}">${word}</a>
                        <br />${unsafeHTML(definition)}
                      </div>`,
                  )}`
            : nothing}
          ${this.selectedCategory === `${this.categoryTab}_${this.unCategorizedId}`
            ? html` <h3>${this.data?.app.unCategorizedLabel}</h3>
                ${this.data?.words
                  .filter((wordObj) => !wordObj.categories)
                  .map(
                    ({ anchor, definition, word }) =>
                      html` <div>
                        <a href="#${anchor}">${word}</a>
                        <br />${unsafeHTML(definition)}
                      </div>`,
                  )}`
            : nothing}
          ${this.selectedCategory !== this.categoryTab &&
          this.selectedCategory !== `${this.categoryTab}_${this.unCategorizedId}`
            ? this.data?.categories
                .filter((category) => {
                  if (this.selectedCategory === this.categoryTab) return category;
                  else return this.selectedCategory === `${this.categoryTab}_${category.id}`;
                })
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(
                  (category) =>
                    html` <h3>${category.name}</h3>
                      ${this.data?.words
                        .filter((wordObj) => wordObj.categories && wordObj.categories.includes(category.id))
                        .map(
                          ({ anchor, definition, word }) =>
                            html` <div>
                              <a href="#${anchor}">${word}</a>
                              <br />${unsafeHTML(definition)}
                            </div>`,
                        )}`,
                )
            : nothing}
        </div>`;
    }

    return nothing;
  }

  private renderLetters() {
    return html` <leaf-tab mobile-variant="dropdown" tab-align="left">
      ${'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map(
        (letter) =>
          html` <leaf-tab-item
              slot="tab"
              label="${this.letterTab}_${letter.toLowerCase()}"
              size="default"
              ?disabled=${this.data?.words.filter(({ word }) => word[0].toLowerCase() === letter.toLowerCase())
                .length === 0}
              tab-align="left"
              >${letter}</leaf-tab-item
            >

            <leaf-tab-section slot="tab-section" label="${this.letterTab}_${letter.toLowerCase()}" has-hr>
              <h4>${letter}</h4>
              <div>
                ${this.data?.words
                  .filter(({ word }) => word[0].toLowerCase() === letter.toLowerCase())
                  .map(
                    ({ anchor, definition, word }) =>
                      html` <div>
                        <a href="#${anchor}">${word}</a>
                        <br />${unsafeHTML(definition)}
                      </div>`,
                  )}
              </div>
            </leaf-tab-section>`,
      )}

      <leaf-tab-item slot="tab" label="|" size="default" disabled tab-align="left">|</leaf-tab-item>
      <leaf-tab-item
        slot="tab"
        scroll-target
        class="focus-visible"
        aria-selected
        label="letter_all"
        active
        size="default"
        >${this.data?.app.allLetterLabel}</leaf-tab-item
      >
      <leaf-tab-section slot="tab-section" label="letter_all" has-hr active>
        ${'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map((letter) => {
          if (this.data?.words.filter(({ word }) => word[0].toLowerCase() === letter.toLowerCase()).length === 0)
            return nothing;

          return html` <h4>${letter}</h4>
            <div>
              ${this.data?.words
                .filter(({ word }) => word[0].toLowerCase() === letter.toLowerCase())
                .map(
                  ({ anchor, definition, word }) =>
                    html` <div>
                      <a href="#${anchor}">${word}</a>
                      <br />${unsafeHTML(definition)}
                    </div>`,
                )}
            </div>`;
        })}
      </leaf-tab-section>
    </leaf-tab>`;
  }

  private renderAliaes() {
    const result =
      this.data?.words.filter((wordObj) => wordObj.aliases && wordObj.aliases.includes(this.selectedAnchor)) || [];
    if (result.length > 0) {
      return html`<div>
        <p>
          ${this.data?.app.alsoKnowAsLabel}
          ${result.map(({ anchor, word }) => html`<a href="#${anchor}">${word}</a>&nbsp;&nbsp;`)}
        </p>
      </div>`;
    } else {
      return html``;
    }
  }

  private renderRelatedTerms() {
    const result =
      this.data?.words.filter(({ anchor }) => this.searchTerm !== '' && anchor === this.selectedAnchor) || [];

    if (result.length > 0 && result[0]?.related) {
      return html`<div>
        <h3>${this.data?.app.relatedTerms}</h3>
        <dl>
          ${result[0].related
            .map((wordRelated) => this.data?.words.find(({ anchor }) => anchor === wordRelated))
            .map(
              (related) =>
                html`<dt>${related?.word}</dt>
                  <dl>${unsafeHTML(related?.definition)}</dl>`,
            )}
        </dl>
      </div>`;
    }

    return nothing;
  }

  protected override firstUpdated() {
    const hash = window.location.hash.substring(1).toLowerCase();
    const [tab] = hash.split('_');

    if (tab === this.categoryTab || tab === this.letterTab) {
      this.activeTab = tab;
    }
    this.unCategorizedId = (this.data?.categories.length || 99) + 1;
    setTimeout(() => this.onHashChange(), 200);
  }

  override render() {
    if (!this.i18n || this.i18n.loading || !this.data) {
      return html`<slot name="loading"></slot>`;
    }

    if (this.i18n?.error || this.error) {
      return html`<slot name="error"></slot>`;
    }

    return html`
      <div class="chcapp-glossary">
        <p>${this.data?.app.searchByLabel}</p>
        <div>
          <leaf-tab mobile-variant="scroll" tab-align="left">
            <leaf-tab-item
              slot="tab"
              ?scroll-target=${this.wordTab === this.activeTab}
              class="${this.wordTab === this.activeTab ? 'focus-visible' : ''}"
              ?aria-selected=${this.wordTab === this.activeTab}
              label="${this.wordTab}"
              ?active=${this.wordTab === this.activeTab}
              size="default"
              >${this.data?.app.wordTab}</leaf-tab-item
            >
            <leaf-tab-section
              slot="tab-section"
              label="${this.wordTab}"
              has-hr
              ?active=${this.wordTab === this.activeTab}
            >
              ${this.renderSearchInput()} ${this.renderWords()}
            </leaf-tab-section>

            <leaf-tab-item
              slot="tab"
              ?scroll-target=${this.categoryTab === this.activeTab}
              class="${this.categoryTab === this.activeTab ? 'focus-visible' : ''}"
              ?aria-selected=${this.categoryTab === this.activeTab}
              label="${this.categoryTab}"
              ?active=${this.categoryTab === this.activeTab}
              size="default"
              >${this.data?.app.categoryTab}</leaf-tab-item
            >
            <leaf-tab-section
              slot="tab-section"
              label="${this.categoryTab}"
              has-hr
              ?active=${this.categoryTab === this.activeTab}
              >${this.renderCategories()}</leaf-tab-section
            >

            <leaf-tab-item
              slot="tab"
              ?scroll-target=${this.letterTab === this.activeTab}
              class="${this.letterTab === this.activeTab ? 'focus-visible' : ''}"
              ?aria-selected=${this.letterTab === this.activeTab}
              label="${this.letterTab}"
              ?active=${this.letterTab === this.activeTab}
              size="default"
              >${this.data?.app.letterTab}</leaf-tab-item
            >
            <leaf-tab-section
              slot="tab-section"
              label="${this.letterTab}"
              has-hr
              ?active=${this.letterTab === this.activeTab}
              >${this.renderLetters()}</leaf-tab-section
            >
          </leaf-tab>
        </div>

        ${this.renderAliaes()} ${this.renderRelatedTerms()}
      </div>
    `;
  }
}
